import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, LogOut } from 'lucide-react';
import { getHub } from '../api/hub';
import { useAuth } from '../hooks/useAuth';
import { clearAllScrollPositions } from '../hooks/useScrollRestoration';
import { ShootCard } from '../components/gallery/ShootCard';
import { SetCard } from '../components/sets/SetCard';
import { CreateSetModal } from '../components/sets/CreateSetModal';
import { UserPair } from '../components/ui/UserChip';
import { SearchPill } from '../components/ui/SearchPill';
import { Logo } from '../components/Logo';
import { imageUrl } from '../api/client';
import { timeAgo, formatAbsoluteDate, greeting } from '../utils/timeAgo';
import type { HubRecentItem, HubShoot } from '../api/setsTypes';
import type { Shoot } from '../api/types';

// Hub — startsidan som ersätter Shoots.tsx som default route.
// Layout: Recent (horizontal scroll) + 2-kolumns Shoots/Sets-grid (2/3+1/3).
export default function Hub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const hubQuery = useQuery({
    queryKey: ['hub'],
    queryFn: () => getHub(),
  });

  // Som med Shoots.tsx: rensa scroll-positioner när vi visar startsidan.
  useEffect(() => {
    clearAllScrollPositions();
  }, []);

  // Global Cmd+K → /search
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/search');
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        setCreateOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const data = hubQuery.data;
  const shoots = data?.shoots ?? [];
  const sets = data?.sets ?? [];
  const recent = data?.recent ?? [];

  const sessionsCount = shoots.length;
  const setsCount = sets.length;

  const yearsRange = useMemo(() => {
    const years = shoots
      .map((s) => {
        const d = s.shootDate ?? s.createdAt;
        if (!d) return NaN;
        return new Date(d).getFullYear();
      })
      .filter((y) => !Number.isNaN(y));
    if (years.length === 0) return null;
    const min = Math.min(...years);
    const max = Math.max(...years);
    return min === max ? String(min) : `${min} — ${max}`;
  }, [shoots]);

  // Beräkna senaste aktivitet för meta
  const lastActivity = useMemo(() => {
    const all = [
      ...shoots.map((s) => s.lastActivityAt ?? s.updatedAt ?? s.createdAt),
      ...sets.map((s) => s.lastActivityAt),
    ].filter(Boolean) as string[];
    if (all.length === 0) return null;
    const sorted = [...all].sort((a, b) => (a < b ? 1 : -1));
    return timeAgo(sorted[0]);
  }, [shoots, sets]);

  return (
    <div className="min-h-screen min-h-[100dvh]">
      {/* Sticky top-nav med global search inline.
          Tidigare delade vi upp nav + search i två rader, men nu lever
          SearchPill direkt i nav (samma mönster som Gallery/SetView). */}
      <nav className="sticky top-0 z-50 flex items-center gap-3 sm:gap-5 px-5 sm:px-10 py-3.5 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border">
        <Link to="/" className="flex-shrink-0" aria-label="Vuelette home">
          <Logo size="md" color="#ededed" />
        </Link>

        {/* Global search: fyller mellanrummet på desktop, ikon på mobil */}
        <div className="flex-1 flex items-center justify-end md:justify-center min-w-0">
          <SearchPill size="full" />
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <Link
            to="/portfolios"
            className="hidden sm:inline-block text-loupe-text-2 hover:text-white text-[11px] tracking-[0.15em] uppercase py-2 px-1 transition-colors"
          >
            Portfolios
          </Link>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="px-3 sm:px-4 py-2.5 bg-white text-black border-none rounded-full text-[11px] sm:text-xs font-semibold tracking-wider uppercase cursor-pointer transition-transform min-h-[40px] inline-flex items-center gap-1.5 whitespace-nowrap hover:scale-[1.03]"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className="hidden sm:inline">New set</span>
            <span className="sm:hidden">New</span>
          </button>
          <button
            type="button"
            onClick={() => logout()}
            title={user ? `Logged in as ${user.displayName ?? user.name}` : 'Sign out'}
            className="text-loupe-text-2 hover:text-white p-2 transition-colors"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <UserPair size="md" />
        </div>
      </nav>

      <div className="px-5 sm:px-10 pt-12 pb-32 max-w-[1600px] mx-auto">
        {/* Page hello */}
        <header className="flex sm:items-end items-start justify-between gap-6 sm:gap-10 flex-col sm:flex-row mb-10">
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl tracking-[-0.04em] leading-[0.95]">
            Good <em className="italic font-normal">{greeting()}</em>
            {user ? `, ${user.displayName ?? capitalize(user.name)}` : ''}
          </h1>
          <div className="text-[11px] sm:text-xs text-loupe-text-3 tracking-[0.12em] uppercase sm:text-right leading-relaxed">
            {sessionsCount} {sessionsCount === 1 ? 'SHOOT' : 'SHOOTS'} · {setsCount}{' '}
            {setsCount === 1 ? 'SET' : 'SETS'}
            {lastActivity ? (
              <>
                <br />
                LAST ACTIVITY {lastActivity.toUpperCase()}
              </>
            ) : null}
          </div>
        </header>

        {/* Recent row */}
        {hubQuery.isLoading ? (
          <RecentSkeleton />
        ) : recent.length > 0 ? (
          <section className="mb-12">
            <div className="flex items-baseline justify-between mb-4 gap-3">
              <h2 className="font-display font-medium text-[22px] tracking-tight">
                <em className="italic font-normal text-loupe-text-2">Recent</em>
              </h2>
              <div className="text-[11px] text-loupe-text-3 tracking-[0.12em] uppercase">
                Mixed · {recent.length} items · sorted by activity
              </div>
            </div>
            <RecentRow items={recent} />
          </section>
        ) : null}

        {/* Two columns */}
        <div className="grid gap-12 lg:gap-14 grid-cols-1 lg:grid-cols-[2fr_1fr]">
          {/* Shoots */}
          <section>
            <div className="flex items-baseline justify-between mb-6 gap-3">
              <h2 className="font-display font-medium text-[28px] tracking-tight">
                Shoots <em className="italic font-normal text-loupe-text-2">· chronological</em>
              </h2>
              <div className="text-[11px] text-loupe-text-3 tracking-[0.12em] uppercase tabular-nums">
                {sessionsCount} sessions{yearsRange ? ` · ${yearsRange}` : ''}
              </div>
            </div>

            {hubQuery.isLoading ? (
              <ShootsSkeleton />
            ) : hubQuery.isError ? (
              <ErrorState onRetry={() => hubQuery.refetch()} />
            ) : shoots.length === 0 ? (
              <EmptyShoots />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-9">
                {shoots.map((s) => (
                  <ShootCard key={s.id} shoot={hubShootToShoot(s)} />
                ))}
              </div>
            )}
          </section>

          {/* Sets */}
          <section>
            <div className="flex items-baseline justify-between mb-6 gap-3">
              <h2 className="font-display font-medium text-[28px] tracking-tight">
                Sets <em className="italic font-normal text-loupe-text-2">· curated</em>
              </h2>
              <div className="text-[11px] text-loupe-text-3 tracking-[0.12em] uppercase tabular-nums">
                {setsCount} {setsCount === 1 ? 'set' : 'sets'}
              </div>
            </div>

            {hubQuery.isLoading ? (
              <SetsSkeleton />
            ) : sets.length === 0 ? (
              <EmptySets onCreate={() => setCreateOpen(true)} />
            ) : (
              <div className="flex flex-col gap-4.5" style={{ rowGap: 18 }}>
                {sets.map((s) => (
                  <SetCard key={s.id} set={s} />
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="mt-20 pt-7 border-t border-loupe-border text-[11px] text-loupe-text-3 tracking-[0.12em] uppercase flex justify-between gap-6 flex-wrap">
          <span>Tap a card to open</span>
          <span>⌘K to search · ⌘N for new set</span>
        </div>
      </div>

      {/* Create modal */}
      {createOpen && data?.workspaceId ? (
        <CreateSetModal
          workspaceId={data.workspaceId}
          onClose={() => setCreateOpen(false)}
          onCreated={(set) => {
            setCreateOpen(false);
            navigate(`/sets/${set.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

// ─── Recent row ──────────────────────────────────────────────────────

function RecentRow({ items }: { items: HubRecentItem[] }) {
  return (
    <div
      className="grid grid-flow-col auto-cols-[180px] sm:auto-cols-[220px] gap-3.5 overflow-x-auto overflow-y-hidden pb-4 -mx-5 px-5 sm:-mx-10 sm:px-10 scrollbar-thin"
      style={{ scrollSnapType: 'x proximity' }}
    >
      {items.map((it) => (
        <RecentCard key={`${it.type}-${it.id}`} item={it} />
      ))}
    </div>
  );
}

function RecentCard({ item }: { item: HubRecentItem }) {
  const isSet = item.type === 'set';
  const to = isSet ? `/sets/${item.id}` : `/shoots/${item.id}`;
  const accent = isSet ? item.color ?? '#5fa8d3' : '#5fa8d3';
  const sub = isSet
    ? `${item.imageCount} frames · ${timeAgo(item.lastActivityAt)}`
    : `${item.imageCount} frames · ${formatAbsoluteDate(item.shootDate ?? item.createdAt)}`;

  return (
    <Link
      to={to}
      className="block text-loupe-text no-underline transition-transform duration-300 hover:-translate-y-1"
      style={{ scrollSnapAlign: 'start', ['--set-accent' as string]: accent }}
    >
      <div className="w-full aspect-[4/5] rounded-[10px] overflow-hidden relative bg-loupe-bg-2 mb-2.5 group">
        {item.coverImageId ? (
          <img
            src={imageUrl(item.coverImageId, 'preview')}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.04] group-hover:brightness-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, transparent 60%), #1a1a1a` }}
          >
            <span className="font-display text-4xl text-white/25">⊞</span>
          </div>
        )}
        <div className="absolute top-2.5 left-2.5 px-2.5 py-1 bg-black/65 backdrop-blur border border-white/10 rounded-full text-[10px] text-white/95 tracking-wider uppercase font-semibold inline-flex items-center gap-1.5">
          <span
            className="text-[11px] leading-none"
            style={{ color: isSet ? accent : 'rgba(255,255,255,0.8)' }}
          >
            {isSet ? '⊞' : '◫'}
          </span>
          {item.type}
        </div>
      </div>
      <div className="font-display text-[15px] sm:text-base font-medium tracking-tight mb-1 truncate">
        {item.name}
      </div>
      <div className="text-[11px] text-loupe-text-3 tracking-wider uppercase tabular-nums">
        {sub}
      </div>
    </Link>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function hubShootToShoot(s: HubShoot): Shoot {
  // ShootCard tar Shoot — vi mappar fält manuellt så den blir typed.
  return {
    id: s.id,
    name: s.name,
    shootDate: s.shootDate,
    date: s.shootDate,
    imageCount: s.imageCount,
    image_count: s.imageCount,
    coverImageId: s.coverImageId ?? null,
    createdAt: s.createdAt,
    created_at: s.createdAt,
  };
}

function capitalize(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

// ─── Skeletons / empty / error states ────────────────────────────────

function RecentSkeleton() {
  return (
    <section className="mb-12">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <h2 className="font-display font-medium text-[22px] tracking-tight">
          <em className="italic font-normal text-loupe-text-2">Recent</em>
        </h2>
      </div>
      <div className="grid grid-flow-col auto-cols-[180px] sm:auto-cols-[220px] gap-3.5 overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="w-full aspect-[4/5] bg-loupe-bg-2 rounded-[10px] mb-2.5" />
            <div className="h-4 bg-loupe-bg-2 rounded w-2/3 mb-1.5" />
            <div className="h-3 bg-loupe-bg-2 rounded w-1/2" />
          </div>
        ))}
      </div>
    </section>
  );
}

function ShootsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-9">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="grid grid-cols-[1.6fr_1fr] gap-1.5 mb-5 h-[280px]">
            <div className="row-span-2 rounded-2xl bg-loupe-bg-2" />
            <div className="rounded-2xl bg-loupe-bg-2" />
            <div className="rounded-2xl bg-loupe-bg-2" />
          </div>
          <div className="h-6 bg-loupe-bg-2 rounded w-1/2 mb-2" />
          <div className="h-3 bg-loupe-bg-2 rounded w-1/3" />
        </div>
      ))}
    </div>
  );
}

function SetsSkeleton() {
  return (
    <div className="flex flex-col gap-4.5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="animate-pulse p-3.5 bg-loupe-bg-2 border border-loupe-border rounded-2xl">
          <div className="grid grid-cols-[120px_1fr] gap-3.5">
            <div className="w-[120px] h-[120px] bg-loupe-bg-3 rounded-[10px]" />
            <div className="flex flex-col gap-2">
              <div className="h-5 bg-loupe-bg-3 rounded w-2/3" />
              <div className="h-3 bg-loupe-bg-3 rounded w-full" />
              <div className="h-3 bg-loupe-bg-3 rounded w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyShoots() {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2">No shoots yet</div>
      <p className="text-loupe-text-2 text-sm">
        Import your first photoshoot via the CLI to see it appear here.
      </p>
    </div>
  );
}

function EmptySets({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-8 text-center">
      <div className="font-display text-xl mb-2">No sets yet</div>
      <p className="text-loupe-text-2 text-sm mb-4">
        Create your first curated selection.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider min-h-[40px] hover:scale-[1.03] transition-transform"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
        New set
      </button>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2 text-michelle">Could not load hub</div>
      <p className="text-loupe-text-2 text-sm mb-4">
        Something went wrong. Make sure the backend is running.
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider"
      >
        Retry
      </button>
    </div>
  );
}
